import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteSuscriptionById, getAllSuscriptions, getPlanById, putSuscriptionById } from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { iDataPlan } from "../../interfaces/SuperAdmin/Pages/iDataPlan";
import {
  iDataSuscripcionOrg,
  PutSuscriptFields,
} from "../../interfaces/SuperAdmin/Pages/iDataSuscripcion";

interface SinglePlanContextProps {
  plan: iDataPlan;
  suscriptions: iDataSuscripcionOrg[];
  selectedSuscript: iDataSuscripcionOrg | null;
  toDeleteSuscr: string;
  isLoading: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  isEditModalOpen: boolean;
  toggleEditModal: (isOpen: boolean) => void;
  handleEdit: (suscription: iDataSuscripcionOrg) => void;
  editSuscription: (
    id_suscripcion: string,
    data: PutSuscriptFields
  ) => Promise<void>;
  assignToDeleteSuscr: (planId: string) => void;
  handleDelete: () => Promise<void>;
}

const SinglePlanContext = createContext<SinglePlanContextProps>(
  {} as SinglePlanContextProps
);

interface Props {
  children: React.ReactNode;
}

const SinglePlanProvider: FC<Props> = ({ children }) => {
  const { idPlan, idOrganization } = useParams();
  const { canAccess } = useAuthContext();
  const [plan, setPlan] = useState<iDataPlan>({} as iDataPlan);
  const [suscriptions, setSuscriptions] = useState<iDataSuscripcionOrg[]>([]);
  const [selectedSuscript, setSelectedSuscript] =
    useState<iDataSuscripcionOrg | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toDeleteSuscr, setToDeleteSuscr] = useState("");

  useEffect(() => {
    const fetchAll = async () => {
      if (idPlan) {
        const isSuperAdmin = canAccess("create:plans");
        const finalOrg = isSuperAdmin ? undefined : idOrganization;
        setIsLoading(true);
        try {
          //Fetch del plan y todas las suscripciones
          const [planRes, susRes] = await Promise.all([
            getPlanById(idPlan),
            getAllSuscriptions(finalOrg, idPlan),
          ]);
          console.log(planRes);

          if (
            planRes.data.statusCode === 200 &&
            susRes.data.statusCode === 200
          ) {
            setPlan(planRes.data.body); // Asignamos el plan

            //Filtramos las suscripciones por el plan. Esto en el futuro debe hacerlo el backend
            const filteredSuscriptions = susRes.data.body.filter(
              (sus) => sus.id_plan === idPlan
            );
            console.log(filteredSuscriptions);
            setSuscriptions(filteredSuscriptions); //Asignamos las suscripciones
          } else {
            toast.error("Error al obtener el plan y sus suscripciones");
          }
        } catch (error) {
          console.log(error);
          toast.error("Error al obtener el plan y sus suscripciones");
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchAll();
  }, [idPlan]);

  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedSuscript(null);
    }
    setIsEditModalOpen(isOpen);
  };

  const handleEdit = (suscription: iDataSuscripcionOrg) => {
    setSelectedSuscript(suscription);
    toggleEditModal(true);
  };

  const editSuscription = async (
    id_suscripcion: string,
    data: PutSuscriptFields
  ) => {
    setIsEditing(true);
    try {
      const res = await putSuscriptionById(data, id_suscripcion);
      console.log(res);

      if (res.data.statusCode === 200) {
        setSuscriptions((prev) =>
          prev.map((suscr) => {
            if (suscr.id_suscripcion === id_suscripcion) {
              return res.data.body;
            }
            return suscr;
          })
        );
        toggleEditModal(false);
        toast.success("Suscripción editada exitosamente");
      } else {
        toast.error("Error al editar la suscripción");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar la suscripción");
    } finally {
      setIsEditing(false);
    }
  };

  const assignToDeleteSuscr = (planId: string) => {
    setToDeleteSuscr(planId);
  };

  const handleDelete = async () => {
    if (toDeleteSuscr) {
      setIsDeleting(true);
      try {
        const res = await deleteSuscriptionById(toDeleteSuscr);
        console.log(res);

        if (res.data.statusCode === 200) {
          setSuscriptions((prev) => {
            const filteredSuscr = prev.filter(
              (suscr) => suscr.id_suscripcion !== toDeleteSuscr
            );
            return filteredSuscr;
          });
          setToDeleteSuscr("");
          toast.success("Suscripción eliminada exitosamente");
        } else {
          toast.error("Error al eliminar la suscripción");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al eliminar la suscripción");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <SinglePlanContext.Provider
      value={{
        plan,
        suscriptions,
        selectedSuscript,
        isLoading,
        isEditing,
        isDeleting,
        isEditModalOpen,
        toDeleteSuscr,
        toggleEditModal,
        handleEdit,
        editSuscription,
        handleDelete,
        assignToDeleteSuscr,
      }}
    >
      {children}
    </SinglePlanContext.Provider>
  );
};

const useSuperAdminSinglePlan = () => useContext(SinglePlanContext);

export { SinglePlanProvider, useSuperAdminSinglePlan };

